import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Badge, Button, Nav, Spinner } from 'react-bootstrap'
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom'
import { signOut } from 'aws-amplify/auth'
import { useDispatch, useSelector } from 'react-redux'
import { fetchSnp500, triggerUpdate, pollJobStatus } from '../../../reducers/snp500Slice'
import Snp500Table from '../../../components/Snp500Table'
import Snp500UpdatePanel from '../../../components/Snp500UpdatePanel'
import Snp500FixesTab from '../../../components/Snp500FixesTab'
import Snp500NoDataTab from '../../../components/Snp500NoDataTab'
import SymbolFixModal from '../../../components/SymbolFixModal'

const POLL_INTERVAL_MS = 5000

function AdminSnp500() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const items = useSelector((state) => state.snp500.items)
  const loading = useSelector((state) => state.snp500.loading)
  const s3LastUpdated = useSelector((state) => state.snp500.s3LastUpdated)
  const jobId = useSelector((state) => state.snp500.jobId)
  const jobStatus = useSelector((state) => state.snp500.jobStatus)
  const unresolved = useSelector((state) => state.snp500.unresolved)
  const fixes = useSelector((state) => state.snp500.fixes)
  const noDataSymbols = useSelector((state) => state.snp500.noDataSymbols)
  const [searchParams] = useSearchParams()
  const pollIntervalRef = useRef(null)
  const [modalKey, setModalKey] = useState(0)
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'list')

  useEffect(() => {
    dispatch(fetchSnp500())
  }, [dispatch])

  useEffect(() => {
    if (jobStatus === 'pending' && jobId) {
      pollIntervalRef.current = setInterval(() => {
        dispatch(pollJobStatus(jobId))
      }, POLL_INTERVAL_MS)
    } else {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
      if (jobStatus === 'complete' && unresolved.length === 0) {
        dispatch(fetchSnp500())
      }
    }
    return () => clearInterval(pollIntervalRef.current)
  }, [jobStatus, jobId, unresolved, dispatch])

  async function handleSignOut() {
    await signOut()
    navigate('/admin')
  }

  function handleUpdate() {
    dispatch(triggerUpdate())
  }

  const pendingFixesCount = useMemo(() => {
    const symbolSet = new Set(items.map((i) => i.Symbol))
    return fixes.filter((f) => symbolSet.has(f.original)).length
  }, [fixes, items])

  return (
    <section className="py-4">
      <header className="mb-4 d-flex justify-content-between align-items-center">
        <div>
          <p
            className="text-uppercase fw-semibold text-muted mb-1"
            style={{ fontSize: '0.7rem', letterSpacing: '0.1em' }}
          >
            Admin
          </p>
          <h2 className="fw-bold mb-0">S&amp;P 500 List</h2>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={handleSignOut}>
          Sign Out
        </Button>
      </header>
      <hr className="opacity-25" />
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/charities">
            Charity Manager
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/sre">
            SRE Events
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/stock">
            Stock Pricing
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/snp500">
            S&amp;P 500 List
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/investments">
            Investments
          </Nav.Link>
        </Nav.Item>
      </Nav>
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link active={activeTab === 'list'} onClick={() => setActiveTab('list')}>
            S&amp;P 500 List
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link active={activeTab === 'fixes'} onClick={() => setActiveTab('fixes')}>
            Fixes
            {pendingFixesCount > 0 && (
              <Badge bg="warning" text="dark" className="ms-1">
                {pendingFixesCount}
              </Badge>
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link active={activeTab === 'no-data'} onClick={() => setActiveTab('no-data')}>
            No-Data Symbols
            {noDataSymbols.length > 0 && (
              <Badge bg="danger" className="ms-1">
                {noDataSymbols.length}
              </Badge>
            )}
          </Nav.Link>
        </Nav.Item>
      </Nav>
      {activeTab === 'list' && (
        <>
          <Snp500UpdatePanel
            s3LastUpdated={s3LastUpdated}
            jobStatus={jobStatus}
            onUpdate={handleUpdate}
          />
          {loading && (
            <div className="d-flex align-items-center gap-2 mb-3 text-muted small">
              <Spinner animation="border" size="sm" role="status" aria-label="Loading">
                <span className="visually-hidden">Loading…</span>
              </Spinner>
            </div>
          )}
          {unresolved.length > 0 && (
            <Button
              variant="warning"
              size="sm"
              className="mb-3"
              onClick={() => setModalKey((k) => k + 1)}
            >
              Review {unresolved.length} Unresolved Symbol{unresolved.length !== 1 ? 's' : ''}
            </Button>
          )}
          <Snp500Table items={items} />
        </>
      )}
      {activeTab === 'fixes' && <Snp500FixesTab />}
      {activeTab === 'no-data' && <Snp500NoDataTab />}
      <SymbolFixModal
        key={modalKey}
        unresolved={unresolved}
        onSuccess={() => dispatch(fetchSnp500())}
      />
    </section>
  )
}

export default AdminSnp500
